import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Search, Loader2, MapPin, Navigation, Plus, GripVertical, ArrowUp, ArrowDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { LeafletMap } from '@/components/transport/LeafletMap';
import { RunningTrips } from '@/components/transport/RunningTrips';
import { ScheduleInfo, fmtIST } from '@/components/transport/ScheduleInfo';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { transportApi, getUser } from '@/services/transportApi';
import { ClubbedInto } from '@/components/transport/ClubbedInfo';

const STATUS = { PENDING: 'bg-amber-100 text-amber-700', ASSIGNED: 'bg-blue-100 text-blue-700', IN_TRANSIT: 'bg-indigo-100 text-indigo-700', UNLOADING: 'bg-fuchsia-100 text-fuchsia-700', COMPLETED: 'bg-green-100 text-green-700', REJECTED: 'bg-red-100 text-red-700', CLUBBED: 'bg-violet-100 text-violet-700' };
const CUSTOM = '__custom__';
const ROAD_FACTOR = 1.3;
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
let _uid = 0;
const nextUid = () => `s${Date.now()}_${_uid++}`;
const newStop = (kind) => ({ uid: nextUid(), kind, label: '', location_id: null, lat: null, lng: null, note: '' });

const haversine = (a, b, c, d) => {
  const R = 6371, toR = (x) => (x * Math.PI) / 180;
  const dLat = toR(c - a), dLng = toR(d - b);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a)) * Math.cos(toR(c)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

const RequestVehicle = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') === 'running' ? 'running' : 'new');
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({ section: (user.sections && user.sections[0]) || '', vehicle_type: '', needed_at: '', purpose: '' });
  const [stops, setStops] = useState([newStop('pickup'), newStop('drop')]);
  const [activeUid, setActiveUid] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [driverPref, setDriverPref] = useState('auto');
  const [mine, setMine] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dupPrompt, setDupPrompt] = useState(null);
  const dragUid = useRef(null);

  const active = stops.find((s) => s.uid === activeUid) || stops.find((s) => s.lat == null) || stops[0];

  const loadMine = useCallback(async () => { try { setMine(await transportApi.listRequests()); } catch { /* ignore */ } }, []);
  useEffect(() => {
    transportApi.getSettings().then((s) => { setTypes(s.vehicle_types || []); setForm((f) => ({ ...f, vehicle_type: f.vehicle_type || (s.vehicle_types || [])[0] || '' })); }).catch(() => {});
    transportApi.listLocations({ active: true }).then(setLocations).catch(() => {});
    transportApi.listSiteSections().then((secs) => {
      setSections(secs);
      const remembered = localStorage.getItem(`twp_last_section_${user.user_id}`);
      const def = (remembered && secs.includes(remembered)) ? remembered : (secs.includes((user.sections || [])[0]) ? user.sections[0] : '');
      setForm((f) => ({ ...f, section: f.section || def }));
    }).catch(() => {});
    loadMine();
    const t = setInterval(loadMine, 15000);
    return () => clearInterval(t);
  }, [loadMine]);

  const patchStop = (uid, patch) => setStops((prev) => prev.map((s) => (s.uid === uid ? { ...s, ...patch } : s)));
  const addStop = (kind) => { const s = newStop(kind); setStops((prev) => [...prev, s]); setActiveUid(s.uid); };
  const removeStop = (uid) => setStops((prev) => prev.filter((s) => s.uid !== uid));
  const moveStop = (uid, dir) => setStops((prev) => {
    const i = prev.findIndex((s) => s.uid === uid); const j = i + dir;
    if (i < 0 || j < 0 || j >= prev.length) return prev;
    const copy = [...prev]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });
  const onDrop = (targetUid) => setStops((prev) => {
    const from = prev.findIndex((s) => s.uid === dragUid.current);
    const to = prev.findIndex((s) => s.uid === targetUid);
    if (from < 0 || to < 0 || from === to) return prev;
    const copy = [...prev]; const [moved] = copy.splice(from, 1); copy.splice(to, 0, moved); return copy;
  });

  const onSelectPlace = (uid, val) => {
    if (val === CUSTOM) { patchStop(uid, { location_id: null }); setActiveUid(uid); return; }
    const loc = locations.find((l) => l.id === val);
    if (loc) patchStop(uid, { lat: loc.lat, lng: loc.lng, label: loc.name, location_id: loc.id });
    setActiveUid(uid);
  };

  const doSearch = async () => {
    if (!q.trim() || q.trim().length < 2) { toast.error('Type an address to search'); return; }
    setSearching(true);
    try {
      const res = await transportApi.geocode(q.trim());
      setResults(res || []);
      if (!res || res.length === 0) toast.message('No matches — try a broader search or tap the map');
    } catch (e) { toast.error(e.response?.data?.detail || 'Address search unavailable'); } finally { setSearching(false); }
  };
  const pickResult = (r) => {
    if (active) patchStop(active.uid, { lat: r.lat, lng: r.lng, label: r.name, location_id: null });
    setResults([]); setQ('');
  };

  const markers = stops.filter((s) => s.lat != null).map((s, i) => ({
    lat: s.lat, lng: s.lng, color: s.kind === 'pickup' ? '#059669' : '#DC2626',
    label: `${s.kind === 'pickup' ? 'Pickup' : 'Drop'} ${i + 1}: ${s.label || ''}`,
    draggable: true, onDragEnd: (la, ln) => patchStop(s.uid, { lat: la, lng: ln, label: s.label || (s.kind === 'pickup' ? 'Pinned pickup' : 'Pinned drop'), location_id: null }),
  }));
  const withCoords = stops.filter((s) => s.lat != null);
  const routeLine = withCoords.length >= 2 ? withCoords.map((s) => [s.lat, s.lng]) : null;
  let estKm = null;
  if (withCoords.length >= 2) {
    let sum = 0;
    for (let i = 0; i < withCoords.length - 1; i++) sum += haversine(withCoords[i].lat, withCoords[i].lng, withCoords[i + 1].lat, withCoords[i + 1].lng);
    estKm = Math.round(sum * ROAD_FACTOR * 10) / 10;
  }

  const buildPayload = () => ({
    section: form.section, vehicle_type: form.vehicle_type, needed_at: form.needed_at, purpose: form.purpose,
    driver_pairing: driverPref === 'auto' ? null : driverPref,
    stops: stops.map((s) => ({ kind: s.kind, label: s.label || '', location_id: s.location_id || null, lat: s.lat, lng: s.lng, note: s.note || '' })),
  });
  const routeSummary = (payloadStops) => {
    const ups = payloadStops.filter((s) => s.kind === 'pickup').map((s) => s.label || 'Pickup');
    const dns = payloadStops.filter((s) => s.kind === 'drop').map((s) => s.label || 'Drop');
    return `${ups.join(', ') || '—'} → ${dns.join(', ') || '—'}`;
  };
  const finalizeSuccess = (res) => {
    try { localStorage.setItem(`twp_last_section_${user.user_id}`, form.section); } catch { /* ignore */ }
    toast.success(res.status === 'ASSIGNED' ? `Vehicle auto-assigned${res.assign_reason ? ' — ' + res.assign_reason : ''}`
      : res.scheduled ? `Request scheduled — a vehicle will be auto-assigned at ${fmtIST(res.assign_after)}`
      : 'Request submitted — pending assignment.');
    setStops([newStop('pickup'), newStop('drop')]); setActiveUid(null);
    setForm((f) => ({ ...f, needed_at: '', purpose: '' }));
    loadMine();
  };
  const submit = async () => {
    if (!form.section.trim() || !form.vehicle_type) { toast.error('Section & vehicle type are required'); return; }
    const hasPickup = stops.some((s) => s.kind === 'pickup');
    const hasDrop = stops.some((s) => s.kind === 'drop');
    if (!hasPickup || !hasDrop) { toast.error('Add at least one pickup and one drop point'); return; }
    if (stops.some((s) => s.lat == null)) { toast.error('Set a map point for every stop (saved place, address search or map tap)'); return; }
    setSubmitting(true);
    try {
      const res = await transportApi.createRequest(buildPayload());
      finalizeSuccess(res);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det && det.code === 'DUPLICATE_ROUTE_PENDING') setDupPrompt({ payload: buildPayload(), info: det });
      else toast.error(typeof det === 'string' ? det : (det?.message || 'Failed to submit'));
    } finally { setSubmitting(false); }
  };
  const confirmDuplicate = async () => {
    if (!dupPrompt) return;
    setSubmitting(true);
    try { const res = await transportApi.createRequest({ ...dupPrompt.payload, allow_duplicate: true }); finalizeSuccess(res); setDupPrompt(null); }
    catch (e) { const det = e.response?.data?.detail; toast.error(typeof det === 'string' ? det : (det?.message || 'Failed to submit')); } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="request-vehicle-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-xl font-bold font-display">Request a Vehicle</h1>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="new" data-testid="req-tab-new">New Request</TabsTrigger>
            <TabsTrigger value="running" data-testid="req-tab-running">Running trips</TabsTrigger>
          </TabsList>
          <TabsContent value="running"><RunningTrips /></TabsContent>
          <TabsContent value="new" className="space-y-6">
        <Card className="border-0 shadow-md"><CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Your Section</Label>
              <Select value={form.section} onValueChange={(v) => setForm((f) => ({ ...f, section: v }))}>
                <SelectTrigger data-testid="req-section-select"><SelectValue placeholder="Choose your section" /></SelectTrigger>
                <SelectContent className="max-h-72">{sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vehicle Type Needed</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_type: v }))}>
                <SelectTrigger data-testid="req-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Stops list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Stops — pickups &amp; drops (drag to reorder; driver can complete in any order)</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-300" onClick={() => addStop('pickup')} data-testid="req-add-pickup-btn"><Plus className="w-3 h-3 mr-1" />Pickup</Button>
                <Button type="button" size="sm" variant="outline" className="h-7 text-red-700 border-red-300" onClick={() => addStop('drop')} data-testid="req-add-drop-btn"><Plus className="w-3 h-3 mr-1" />Drop</Button>
              </div>
            </div>
            <div className="space-y-2" data-testid="req-stops-list">
              {stops.map((s, i) => (
                <div key={s.uid} draggable onDragStart={() => { dragUid.current = s.uid; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(s.uid)}
                  className={`rounded-lg border p-2.5 flex items-start gap-2 bg-white ${active?.uid === s.uid ? 'ring-2 ring-amber-400' : ''}`} data-testid={`req-stop-row-${i}`}>
                  <div className="flex flex-col items-center pt-1 text-gray-300 cursor-grab"><GripVertical className="w-4 h-4" /><span className="text-[10px] font-bold text-gray-400">{i + 1}</span></div>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="inline-flex rounded-md overflow-hidden border text-[11px]">
                        <button type="button" onClick={() => patchStop(s.uid, { kind: 'pickup' })} className={`px-2 py-0.5 ${s.kind === 'pickup' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'}`} data-testid={`req-stop-kind-pickup-${i}`}>Pickup</button>
                        <button type="button" onClick={() => patchStop(s.uid, { kind: 'drop' })} className={`px-2 py-0.5 ${s.kind === 'drop' ? 'bg-red-600 text-white' : 'bg-white text-gray-600'}`} data-testid={`req-stop-kind-drop-${i}`}>Drop</button>
                      </div>
                      <Select value={s.location_id || CUSTOM} onValueChange={(v) => onSelectPlace(s.uid, v)}>
                        <SelectTrigger className="h-7 w-44 text-xs" data-testid={`req-stop-place-${i}`}><SelectValue placeholder="Saved place" /></SelectTrigger>
                        <SelectContent>
                          {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                          <SelectItem value={CUSTOM}>Custom — search / tap map</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input value={s.label} onChange={(e) => patchStop(s.uid, { label: e.target.value })} placeholder="Label" className="h-7 text-xs flex-1 min-w-[120px]" data-testid={`req-stop-label-${i}`} />
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500">
                      <MapPin className={`w-3 h-3 ${s.kind === 'pickup' ? 'text-emerald-600' : 'text-red-600'}`} />
                      <span data-testid={`req-stop-coords-${i}`}>{s.lat != null ? `${Number(s.lat).toFixed(3)}, ${Number(s.lng).toFixed(3)}` : 'No point set'}</span>
                      <button type="button" onClick={() => setActiveUid(s.uid)} className={`underline ${active?.uid === s.uid ? 'text-amber-600 font-semibold' : 'text-blue-600'}`} data-testid={`req-stop-setmap-${i}`}>{active?.uid === s.uid ? 'Setting on map…' : 'Set on map'}</button>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button type="button" onClick={() => moveStop(s.uid, -1)} className="p-1 text-gray-400 hover:text-gray-700" data-testid={`req-stop-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => moveStop(s.uid, 1)} className="p-1 text-gray-400 hover:text-gray-700" data-testid={`req-stop-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => removeStop(s.uid)} className="p-1 text-red-400 hover:text-red-600" data-testid={`req-stop-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map */}
          <div>
            <Label>Map — tap to set the {active ? `${active.kind} "${active.label || 'stop ' + (stops.indexOf(active) + 1)}"` : 'active stop'}</Label>
            <div className="flex gap-2 my-2">
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()} placeholder="Search an address for the active stop" data-testid="req-search-input" />
              <Button variant="outline" onClick={doSearch} disabled={searching} data-testid="req-search-btn">{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
            {results.length > 0 && (
              <div className="mb-2 border rounded-lg divide-y max-h-36 overflow-y-auto" data-testid="req-search-results">
                {results.map((r, i) => (
                  <button key={i} onClick={() => pickResult(r)} className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50" data-testid={`req-result-${i}`}>
                    <span className="font-medium">{r.name}</span><span className="text-gray-400"> — {r.display_name}</span>
                  </button>
                ))}
              </div>
            )}
            <LeafletMap height="300px" markers={markers} routeLine={routeLine}
              onMapClick={(la, ln) => { if (active) patchStop(active.uid, { lat: la, lng: ln, label: active.label || (active.kind === 'pickup' ? 'Pinned pickup' : 'Pinned drop'), location_id: null }); }}
              testId="req-map" />
          </div>

          {estKm != null && (
            <div className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3" data-testid="req-estimate">
              <Navigation className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-semibold" data-testid="req-estimate-km">Estimated distance: ~{estKm} km across {withCoords.length} stops</p>
                <p className="text-xs text-gray-500">Estimate only (for assignment). The billed distance comes from the odometer.</p>
              </div>
            </div>
          )}

          <div><Label>Needed At</Label><Input type="datetime-local" value={form.needed_at} onChange={(e) => setForm((f) => ({ ...f, needed_at: e.target.value }))} data-testid="req-needed-input" />
            <p className="text-[11px] text-gray-500 mt-1" data-testid="req-needed-hint">Leave empty for now. A later time is scheduled — the vehicle is auto-assigned 60 min before.</p></div>
          <div>
            <Label>Driver preference</Label>
            <Select value={driverPref} onValueChange={setDriverPref}>
              <SelectTrigger data-testid="req-driver-pref-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (use the depot's default)</SelectItem>
                <SelectItem value="fairness">Fairest — rotate to fewest-km driver</SelectItem>
                <SelectItem value="nearest">Nearest — driver closest to the vehicle (speed)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Purpose (optional)</Label><Textarea value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))} rows={2} data-testid="req-purpose-input" /></div>
          <Button onClick={submit} disabled={submitting} className="bg-amber-500 hover:bg-amber-600" data-testid="req-submit-btn"><Send className="w-4 h-4 mr-1" />Submit Request</Button>
        </CardContent></Card>

        <div>
          <h3 className="text-sm font-semibold text-gray-600 mb-2">My Requests</h3>
          <Card className="border-0 shadow-md"><CardContent className="p-0">
            <Table data-testid="my-requests-table">
              <TableHeader><TableRow>
                <TableHead>Route</TableHead><TableHead>Type</TableHead><TableHead>Est.</TableHead><TableHead>Vehicle</TableHead><TableHead>Driver</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {mine.map((r) => (
                  <TableRow key={r.id} data-testid={`my-request-row-${r.id}`}>
                    <TableCell className="text-sm">{r.route_summary || `${r.pickup} → ${r.drop}`}</TableCell>
                    <TableCell>{r.vehicle_type}</TableCell>
                    <TableCell className="text-xs">{r.estimated_distance_km != null ? `~${r.estimated_distance_km} km` : '—'}</TableCell>
                    <TableCell>{r.vehicle_label || '—'}</TableCell>
                    <TableCell>{r.driver_name || '—'}</TableCell>
                    <TableCell>
                      <Badge className={r.scheduled ? 'bg-indigo-100 text-indigo-700' : (STATUS[r.status] || '')} data-testid={`my-request-status-${r.id}`}>{r.scheduled ? 'SCHEDULED' : r.status}</Badge>
                      <ScheduleInfo r={r} testId={`my-request-schedule-${r.id}`} />
                      <ClubbedInto r={r} testId={`my-request-clubbed-${r.id}`} />
                      {r.eta && <div className="text-[11px] text-teal-600 mt-1 whitespace-nowrap" data-testid={`my-request-eta-${r.id}`}>ETA ~{fmtTime(r.eta.eta_at)} · ~{r.eta.remaining_km} km</div>}
                    </TableCell>
                  </TableRow>
                ))}
                {mine.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8" data-testid="my-requests-empty">No requests yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!dupPrompt} onOpenChange={(o) => !o && setDupPrompt(null)}>
        <DialogContent data-testid="dup-route-dialog">
          <DialogHeader>
            <DialogTitle>Route already pending</DialogTitle>
            <DialogDescription>This route has already been logged and is pending.</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-gray-600">
            <p className="font-semibold text-[#1A1F2E]">{dupPrompt && routeSummary(dupPrompt.payload.stops)}</p>
            <ul className="mt-2 space-y-1">
              {(dupPrompt?.info?.existing || []).map((x) => (
                <li key={x.id} className="flex items-center justify-between gap-2 border rounded px-2 py-1.5" data-testid={`dup-existing-${x.id}`}>
                  <span className="text-xs">{x.section || '—'} · {x.requested_by_name || '—'}<span className="text-gray-400"> · {fmtTime(x.created_at)}</span></span>
                  <Badge className={STATUS[x.status] || ''}>{x.status}</Badge>
                </li>
              ))}
            </ul>
            <p className="mt-3 font-medium text-[#1A1F2E]">Do you want to add another trip?</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupPrompt(null)} data-testid="dup-cancel-btn">No, cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={confirmDuplicate} disabled={submitting} data-testid="dup-confirm-btn">Yes, add another trip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestVehicle;
